/**
 * The decision table, wired into Trystero's handshake hook.
 *
 * Trystero runs this after a peer connection is up and before the peer is announced. Returning
 * accepts the peer, throwing destroys it and reports through `onJoinError`. That is the seam the
 * star topology and the collision rule are built on: nothing downstream has to filter a peer list,
 * because the pairs that should not exist never become peers.
 *
 * Both sides send first and then read, which is safe here because the transport underneath is
 * already connected and buffered. Ordering the exchange by `isInitiator` would only matter if one
 * side had to speak before the other could answer, and neither does. It is also why a returning
 * guest's seat claim is made over its own nonce rather than the host's: there is no host nonce yet
 * when the hello carrying the claim is composed.
 *
 * Free seats are read twice for the same reason. What goes in the hello is what this side can offer
 * anybody, and what the decision is made on is what it can offer this peer, which is one more when
 * the peer has just proved that one of the held chairs is its own.
 */

import { NetError } from '@/net/session/sessionErrors';
import { MESSAGE_KINDS } from '@/net/wire/messageKinds';
import { decideHandshake } from '@/net/session/sessionRole';
import { decodeFrame, encodeFrame } from '@/net/wire/frameCodec';
import { decodeHello, encodeHello } from '@/net/wire/helloFrame';
import { isEmptyProof } from '@/net/wire/seatProof';
import type { HandshakeDecision } from '@/net/session/sessionRole';
import type { PeerHandshake } from 'trystero';
import type { RoleClaim } from '@/net/wire/helloFrame';
import type { SessionRole } from '@/net/transportContract';

export interface HandshakeContext {
  /** Read at handshake time: a host that was probing may have promoted since the room opened. */
  claim(): RoleClaim;
  /** True when this guest already accepted a host. */
  hasHost(): boolean;
  /** How long this side has held its claim on the code. */
  heldMs(): number;
  /** Distinguishes two attempts by the same peer id when a connection flaps. */
  nonce(): number;
  /**
   * Seats this side can still offer, read at handshake time.
   *
   * Counted from the roles this table has already accepted rather than from the transport's peer
   * list, because a peer is announced some time after it is accepted here, and four handshakes
   * running at once would otherwise all see four empty chairs.
   */
  freeSeats(claimedByPeer: boolean): number;
  /** This side's claim on a seat, over the nonce going out with it. Zeroes when there is none. */
  seatProof(nonce: number): Promise<Uint8Array>;
  /** Whether a peer's claim matches a seat this side is keeping. Always false on a guest. */
  claimsHeldSeat(nonce: number, proof: Uint8Array): Promise<boolean>;
  onAccepted(peerId: string, peerRole: SessionRole): void;
  /** Every refusal, so the transport can tell its own refusals from Trystero's own errors. */
  onRefused(peerId: string, decision: Extract<HandshakeDecision, { accept: false }>): void;
}

export function createPeerHandshake(context: HandshakeContext): PeerHandshake {
  return async (peerId, send, receive, isInitiator): Promise<void> => {
    const claim = context.claim();
    const nonce = context.nonce();
    const seatProof = await context.seatProof(nonce);
    const hello = encodeFrame(
      MESSAGE_KINDS.hello,
      encodeHello({
        role: claim,
        heldMs: context.heldMs(),
        nonce,
        freeSeats: context.freeSeats(false),
        seatProof,
      }),
    );

    await send(hello.buffer);
    const { data } = await receive();
    const frame = decodeFrame(toBytes(data));
    if (frame.kind !== MESSAGE_KINDS.hello) {
      throw new NetError('malformedFrame', 'a peer opened with something other than a hello');
    }

    const remote = decodeHello(frame.payload);
    const claimedByPeer = await context.claimsHeldSeat(remote.nonce, remote.seatProof);
    const decision = decideHandshake({
      selfClaim: claim,
      remoteClaim: remote.role,
      remoteVersion: frame.version,
      isInitiator,
      selfHeldMs: context.heldMs(),
      remoteHeldMs: remote.heldMs,
      hasHost: context.hasHost(),
      selfFreeSeats: context.freeSeats(claimedByPeer),
      remoteFreeSeats: remote.freeSeats,
      selfClaimsSeat: !isEmptyProof(seatProof),
    });

    if (!decision.accept) {
      context.onRefused(peerId, decision);
      throw new NetError(decision.code, decision.detail);
    }
    context.onAccepted(peerId, decision.peerRole);
  };
}

/** Trystero hands back whatever the peer sent. Anything that is not bytes is not our protocol. */
function toBytes(data: unknown): Uint8Array {
  if (data instanceof ArrayBuffer) return new Uint8Array(data);
  if (ArrayBuffer.isView(data)) {
    return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  }
  throw new NetError('malformedFrame', 'a peer sent something that is not a frame');
}
